import {
  ADD_ELEMENT_BUTTON,
  ADD_ELEMENT_GALLERY,
  ADD_FORM_SUBMIT_BUTTON,
  INSPECT_ELEMENT_JSON,
  ADD_ELEMENT_GRAPHIC,
  ADD_ELEMENT_ICON,
  ADD_ELEMENT_IFRAME,
  ADD_ELEMENT_LINE,
  ADD_ELEMENT_OVAL,
  ADD_ELEMENT_SIMPLE,
  ADD_ELEMENT_LOTTIE,
  ADD_ELEMENT_SOCIAL_GRID,
  ADD_ELEMENT_VIDEO,
  ADD_TEXT_ELEMENT_HEADING,
  ADD_TEXT_ELEMENT_PARAGRAPH,
  ADD_TEXT_ELEMENT_SUBHEADING,
  // Add element actions
  ADD_TEXT_ELEMENT_TITLE,
  ADD_TO_GROUP,
  ALIGN_ELEMENT_TO_CANVAS_BOTTOM,
  // Alignment actions
  ALIGN_ELEMENT_TO_CANVAS_HORIZONTAL,
  ALIGN_ELEMENT_TO_CANVAS_LEFT,
  ALIGN_ELEMENT_TO_CANVAS_RIGHT,
  ALIGN_ELEMENT_TO_CANVAS_TOP,
  ALIGN_ELEMENT_TO_CANVAS_VERTICAL,
  ALIGN_SELECTED_ELEMENTS_BOTTOM,
  ALIGN_SELECTED_ELEMENTS_HORIZONTAL,
  ALIGN_SELECTED_ELEMENTS_LEFT,
  ALIGN_SELECTED_ELEMENTS_RIGHT,
  ALIGN_SELECTED_ELEMENTS_TOP,
  ALIGN_SELECTED_ELEMENTS_VERTICAL,
  // Arrange actions
  BRING_FORWARD,
  BRING_TO_FRONT,
  SEND_BACKWARD,
  SEND_TO_BACK,
  // Element actions
  COPY,
  CROP_IMAGE,
  CUT,
  DELETE_CANVAS,
  DELETE_ELEMENT,
  DISTRIBUTE_ELEMENTS_HORIZONTAL,
  DISTRIBUTE_ELEMENTS_VERTICAL,
  DUPLICATE_CANVAS,
  DUPLICATE_ELEMENT,
  // Group actions
  GROUP_ELEMENTS,
  LAYOUT_MOBILE_CANVAS,
  // Shared actions
  PASTE_ELEMENT,
  REMOVE_FROM_GROUP,
  RENAME_CANVAS,
  RENAME_ELEMENT,
  RESET_IMAGE,
  // Canvas actions
  SELECT_CANVAS,
  SELECT_SITE_CANVAS,
  SWAP_IMAGE,
  TOGGLE_LOCK_ELEMENT,
  UNGROUP_ELEMENTS,
} from "@/constants/ActionConstants";
import AppConstants from "@/constants/AppConstants";
import { FORM_FIELD_MENU_ITEMS, FORM_SUBMIT_MENU_ITEM } from "@/constants/FormFieldConstants.mjs";
import SelectionStore from "@/stores/SelectionStore";
import {
  canRemoveFromGroup,
  canSelectedGroupDissolve,
  canSelectionGroup,
  canSelectionJoinGroup,
} from "@/utils/GroupUtils";
import BaseRightClickMenuConfig from "./BaseRightClickMenuConfig";
import { getModSymbol } from "@/utils/HTMLUtils";
import LocalStorageService from "@/service/LocalStorageService";

const { ELEMENT_TYPE_GRAPHIC } = AppConstants;
const MOD_SYMBOL = getModSymbol();

/**
 * Returns selected elements if the context is a valid element selection, or null otherwise.
 */
const getGroupActionElements = (context) => {
  if (
    !BaseRightClickMenuConfig.isElement(context) ||
    BaseRightClickMenuConfig.isUnselectedBlock(context)
  )
    return null;
  const selectedBlock = SelectionStore.getBlock();
  return selectedBlock?.getIn(["selected", "elements"]) ?? null;
};

/**
 * Whether new elements can be added: a selected block that isn't a site canvas instance.
 * @param {Object} context
 * @returns {boolean}
 */
const canAddElements = (context) =>
  !BaseRightClickMenuConfig.isUnselectedBlock(context) &&
  !BaseRightClickMenuConfig.isSiteBlockInstance(context);

class StageRightClickMenuConfig extends BaseRightClickMenuConfig {
  constructor() {
    super();
    this.setupMenuStructure();
  }

  setupMenuStructure() {
    // Unselected block options
    this.addMenuItem({
      action: SELECT_CANVAS,
      icon: "shapeElements",
      label: "Select Canvas",
      conditions: BaseRightClickMenuConfig.isUnselectedBlock,
      order: 10,
    });

    // Add Text and Add Elements submenus (only for non-site-block-instances and not unselected blocks)
    this.addSubmenu({
      label: "Add Text",
      icon: "textElement",
      order: 19,
      conditions: canAddElements,
      items: [
        { action: ADD_TEXT_ELEMENT_TITLE, icon: "textElement", label: "Title" },
        {
          action: ADD_TEXT_ELEMENT_HEADING,
          icon: "textElement",
          label: "Heading",
        },
        {
          action: ADD_TEXT_ELEMENT_SUBHEADING,
          icon: "textElement",
          label: "Subheading",
        },
        {
          action: ADD_TEXT_ELEMENT_PARAGRAPH,
          icon: "textElement",
          label: "Paragraph",
        },
      ],
    });

    this.addSubmenu({
      label: "Add Elements",
      icon: "shapeElements",
      order: 20,
      conditions: canAddElements,
      items: [
        { action: ADD_ELEMENT_LINE, icon: "lineElement", label: "Line" },
        {
          action: ADD_ELEMENT_SIMPLE,
          icon: "squareElement",
          label: "Rectangle",
        },
        { action: ADD_ELEMENT_OVAL, icon: "ellipse", label: "Oval" },
        { action: ADD_ELEMENT_ICON, icon: "symbolElement", label: "Icon" },
        { action: ADD_ELEMENT_BUTTON, icon: "buttonElement", label: "Button" },
        { action: ADD_ELEMENT_GRAPHIC, icon: "mediaLibrary", label: "Image" },
        {
          action: ADD_ELEMENT_GALLERY,
          icon: "galleryElement",
          label: "Gallery",
        },
        { action: ADD_ELEMENT_VIDEO, icon: "videoElement", label: "Video" },
        { action: ADD_ELEMENT_LOTTIE, icon: "lottieFiles", label: "Lottie" },
        {
          action: ADD_ELEMENT_SOCIAL_GRID,
          icon: "socialGridElement",
          label: "Social Grid",
        },
        {
          action: ADD_ELEMENT_IFRAME,
          icon: "embedElement",
          label: "Embed Code",
        },
      ],
    });

    // isFormFieldsEnabled is resolved by StageRightClickMenu
    this.addSubmenu({
      label: "Add Form Fields",
      icon: "formFieldText",
      order: 21,
      conditions: (context, additionalContext) =>
        Boolean(additionalContext?.isFormFieldsEnabled) && canAddElements(context),
      items: [
        ...FORM_FIELD_MENU_ITEMS.map(({ action, icon, label }) => ({ action, icon, label })),
        { type: "divider" },
        { action: ADD_FORM_SUBMIT_BUTTON, ...FORM_SUBMIT_MENU_ITEM },
      ],
    });

    // First divider after Add Elements
    this.addDivider(30);

    // Shared actions (Paste)
    this.addMenuItem({
      action: PASTE_ELEMENT,
      icon: "paste",
      hotkey: `${MOD_SYMBOL}V`,
      label: (_context, additionalContext) => {
        const canPasteElements = additionalContext?.canPasteElements;
        return `Paste ${canPasteElements?.payload?.length > 1 ? "Elements" : "Element"}`;
      },
      conditions: (context, additionalContext) => {
        const canPasteElements = additionalContext?.canPasteElements;
        return (
          !BaseRightClickMenuConfig.isUnselectedBlock(context) &&
          !BaseRightClickMenuConfig.isSiteBlockInstance(context) &&
          Boolean(canPasteElements)
        );
      },
      order: 40,
    });

    // Canvas actions
    this.addMenuItem({
      action: RENAME_CANVAS,
      icon: "edit",
      label: "Rename Canvas",
      conditions: (context) =>
        BaseRightClickMenuConfig.isBlock(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      order: 50,
    });

    this.addMenuItem({
      action: SELECT_SITE_CANVAS,
      icon: "siteCanvas",
      label: "Edit Site Canvas",
      conditions: (context) =>
        BaseRightClickMenuConfig.isBlock(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context) &&
        BaseRightClickMenuConfig.isSiteBlockInstance(context),
      order: 51,
    });

    this.addMenuItem({
      action: LAYOUT_MOBILE_CANVAS,
      icon: "mobileDevice",
      label: "Layout Mobile",
      conditions: (context) =>
        BaseRightClickMenuConfig.isBlock(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context) &&
        !BaseRightClickMenuConfig.isSiteBlockInstance(context),
      order: 52,
    });

    this.addMenuItem({
      action: DUPLICATE_CANVAS,
      icon: "copy",
      label: "Duplicate Canvas",
      conditions: (context) =>
        BaseRightClickMenuConfig.isBlock(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context) &&
        !context.context?.isSiteBlock,
      order: 53,
    });

    // Element actions
    this.addMenuItem({
      action: SWAP_IMAGE,
      icon: "swapValues",
      label: "Swap Image",
      disabled: BaseRightClickMenuConfig.isLocked,
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        BaseRightClickMenuConfig.isSingleElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context) &&
        context.entity?.get("type") === ELEMENT_TYPE_GRAPHIC,
      order: 40,
    });

    this.addMenuItem({
      action: RESET_IMAGE,
      icon: "imageReset",
      label: "Reset Image",
      disabled: BaseRightClickMenuConfig.isLocked,
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        BaseRightClickMenuConfig.isSingleElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context) &&
        context.entity?.get("type") === ELEMENT_TYPE_GRAPHIC,
      order: 41,
    });

    this.addMenuItem({
      action: CROP_IMAGE,
      icon: "crop",
      label: "Crop Image",
      disabled: BaseRightClickMenuConfig.isLocked,
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        BaseRightClickMenuConfig.isSingleElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context) &&
        context.entity?.get("type") === ELEMENT_TYPE_GRAPHIC,
      order: 42,
    });

    this.addMenuItem({
      action: COPY,
      icon: "copy",
      hotkey: `${MOD_SYMBOL}C`,
      label: "Copy Element",
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      order: 60,
    });

    this.addMenuItem({
      action: CUT,
      icon: "cut",
      label: "Cut Element",
      hotkey: `${MOD_SYMBOL}X`,
      disabled: BaseRightClickMenuConfig.isLocked,
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      order: 61,
    });

    this.addMenuItem({
      action: DUPLICATE_ELEMENT,
      icon: "copy",
      label: "Duplicate Element",
      hotkey: `${MOD_SYMBOL}D`,
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      order: 62,
    });

    this.addMenuItem({
      action: RENAME_ELEMENT,
      icon: "edit",
      label: "Rename Element",
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        BaseRightClickMenuConfig.isSingleElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      order: 63,
    });

    this.addMenuItem({
      action: TOGGLE_LOCK_ELEMENT,
      icon: (context) => (BaseRightClickMenuConfig.isLocked(context) ? "unlock" : "lock"),
      hotkey: `${MOD_SYMBOL}L`,
      label: (context) =>
        `${BaseRightClickMenuConfig.isLocked(context) ? "Unlock" : "Lock"} Element`,
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        BaseRightClickMenuConfig.isSingleElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      order: 64,
    });

    // Arrange submenu
    this.addSubmenu({
      label: "Arrange Layers",
      icon: "showLayers",
      order: 65,
      disabled: BaseRightClickMenuConfig.isLocked,
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      items: [
        {
          action: BRING_TO_FRONT,
          icon: "bringToFront",
          label: "Bring to Front",
          hotkey: `${MOD_SYMBOL}⌥]`,
        },
        {
          action: BRING_FORWARD,
          icon: "bringForward",
          label: "Bring Forward",
          hotkey: `${MOD_SYMBOL}]`,
        },
        {
          action: SEND_BACKWARD,
          icon: "sendBackward",
          label: "Send Backward",
          hotkey: `${MOD_SYMBOL}[`,
        },
        {
          action: SEND_TO_BACK,
          icon: "sendToBack",
          label: "Send to Back",
          hotkey: `${MOD_SYMBOL}⌥[`,
        },
      ],
    });

    // Align to Canvas submenu
    this.addSubmenu({
      label: "Align To Canvas",
      icon: "alignCanvasHorizCenter",
      order: 70,
      disabled: BaseRightClickMenuConfig.isLocked,
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      items: [
        { action: ALIGN_ELEMENT_TO_CANVAS_TOP, icon: "alignTop", label: "Top" },
        {
          action: ALIGN_ELEMENT_TO_CANVAS_VERTICAL,
          icon: "alignCenterVert",
          label: "Center Vertical",
        },
        {
          action: ALIGN_ELEMENT_TO_CANVAS_BOTTOM,
          icon: "alignBottom",
          label: "Bottom",
        },
        { type: "divider" },
        {
          action: ALIGN_ELEMENT_TO_CANVAS_LEFT,
          icon: "alignLeft",
          label: "Left",
        },
        {
          action: ALIGN_ELEMENT_TO_CANVAS_HORIZONTAL,
          icon: "alignCenterHoriz",
          label: "Center Horizontal",
        },
        {
          action: ALIGN_ELEMENT_TO_CANVAS_RIGHT,
          icon: "alignRight",
          label: "Right",
        },
      ],
    });

    // Align Selection submenu (for multiple elements)
    this.addSubmenu({
      label: "Align Selection",
      icon: "shapeElements",
      className: "align-selection",
      order: 80,
      disabled: BaseRightClickMenuConfig.isLocked,
      conditions: (context) =>
        BaseRightClickMenuConfig.isMultipleElements(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      items: [
        { action: ALIGN_SELECTED_ELEMENTS_TOP, icon: "alignTop", label: "Top" },
        {
          action: ALIGN_SELECTED_ELEMENTS_VERTICAL,
          icon: "alignCenterVert",
          label: "Center Vertical",
        },
        {
          action: ALIGN_SELECTED_ELEMENTS_BOTTOM,
          icon: "alignBottom",
          label: "Bottom",
        },
        { type: "divider" },
        {
          action: ALIGN_SELECTED_ELEMENTS_LEFT,
          icon: "alignLeft",
          label: "Left",
        },
        {
          action: ALIGN_SELECTED_ELEMENTS_HORIZONTAL,
          icon: "alignCenterHoriz",
          label: "Center Horizontal",
        },
        {
          action: ALIGN_SELECTED_ELEMENTS_RIGHT,
          icon: "alignRight",
          label: "Right",
        },
        { type: "divider" },
        {
          action: DISTRIBUTE_ELEMENTS_HORIZONTAL,
          icon: "distHoriz",
          label: "Distribute Horizontally",
        },
        {
          action: DISTRIBUTE_ELEMENTS_VERTICAL,
          icon: "distVert",
          label: "Distribute Vertically",
        },
      ],
    });

    // Group actions (dynamic based on selection state)
    this.addMenuItem({
      action: GROUP_ELEMENTS,
      icon: "group",
      hotkey: `${MOD_SYMBOL}G`,
      label: "Group Elements",
      conditions: (context) => {
        const elements = getGroupActionElements(context);
        return elements ? canSelectionGroup(elements) : false;
      },
      order: 90,
    });

    this.addMenuItem({
      action: UNGROUP_ELEMENTS,
      icon: "unGroup",
      hotkey: `${MOD_SYMBOL}⇧G`,
      label: "Ungroup Elements",
      conditions: (context) => {
        const elements = getGroupActionElements(context);
        return elements ? canSelectedGroupDissolve(elements) : false;
      },
      order: 90,
    });

    this.addMenuItem({
      action: ADD_TO_GROUP,
      icon: "addToGroup",
      label: "Add to Group",
      conditions: (context) => {
        const elements = getGroupActionElements(context);
        return elements ? canSelectionJoinGroup(elements) : false;
      },
      order: 90,
    });

    this.addMenuItem({
      action: REMOVE_FROM_GROUP,
      icon: "removeFromGroup",
      label: "Remove from Group",
      conditions: (context) => {
        const elements = getGroupActionElements(context);
        return elements ? canRemoveFromGroup(elements) : false;
      },
      order: 90,
    });

    // Final divider before delete
    this.addDivider(95);

    // Delete actions (always last)
    this.addMenuItem({
      action: DELETE_CANVAS,
      icon: "trash",
      label: "Delete Canvas",
      className: "context-menu-delete",
      conditions: (context) =>
        BaseRightClickMenuConfig.isBlock(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      order: 100,
    });

    this.addMenuItem({
      action: DELETE_ELEMENT,
      icon: "trash",
      label: "Delete Element",
      className: "context-menu-delete",
      disabled: BaseRightClickMenuConfig.isLocked,
      conditions: (context) =>
        BaseRightClickMenuConfig.isElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      order: 100,
    });

    this.addDivider(105);

    this.addMenuItem({
      action: INSPECT_ELEMENT_JSON,
      icon: "codeElement",
      label: "Inspect Element JSON",
      conditions: (context) =>
        LocalStorageService.get(AppConstants.LOCAL_STORE_DEV_TOOLS) === "true" &&
        BaseRightClickMenuConfig.isElement(context) &&
        BaseRightClickMenuConfig.isSingleElement(context) &&
        !BaseRightClickMenuConfig.isUnselectedBlock(context),
      order: 110,
    });
  }

  /**
   * Get menu items with additional context like paste capability
   */
  getMenuItems(context, additionalContext = {}) {
    return super.getMenuItems(context, additionalContext).map((item) => {
      // Create a copy to avoid mutating the original
      const itemCopy = { ...item };

      // Handle dynamic labels, icons, and disabled state
      if (typeof itemCopy.label === "function") {
        itemCopy.label = itemCopy.label(context, additionalContext);
      }
      if (typeof itemCopy.icon === "function") {
        itemCopy.icon = itemCopy.icon(context);
      }
      if (typeof itemCopy.disabled === "function") {
        itemCopy.disabled = itemCopy.disabled(context);
      }

      // Add plural suffix for elements (avoid duplicate replacements)
      if (
        BaseRightClickMenuConfig.isElement(context) &&
        !BaseRightClickMenuConfig.isSingleElement(context) &&
        (itemCopy.action === DELETE_ELEMENT || itemCopy.label?.includes("Element")) &&
        !itemCopy.label?.includes("Elements") // Don't replace if already plural
      ) {
        itemCopy.label = itemCopy.label?.replace("Element", "Elements");
      }

      return itemCopy;
    });
  }
}

export default StageRightClickMenuConfig;
